import React from "react";
import { motion } from "framer-motion";
import { Phone, Printer, Mail, MapPin } from "lucide-react";
import { SITE } from "../../../lib/siteData";
import { fadeUp } from "../../../lib/motion";
import EmbeddedForm from "../EmbeddedForm";
import SocialIcons from "../SocialIcons";

function QuoteContactItem({ icon: Icon, label, value, href }) {
  const content = (
    <span className="block break-words text-[14px] text-white hover:text-[#B5BAC2] transition-colors">
      {value}
    </span>
  );
  return (
    <div className="flex items-start gap-3">
      <span className="mt-0.5 inline-flex items-center justify-center w-9 h-9 rounded-full bg-white/8 text-[#B5BAC2] flex-shrink-0">
        <Icon size={15} />
      </span>
      <div className="min-w-0">
        <p className="text-[11px] uppercase tracking-[0.22em] text-white/65 font-semibold">
          {label}
        </p>
        {href ? <a href={href}>{content}</a> : content}
      </div>
    </div>
  );
}

export default function QuoteSection() {
  return (
    <section
      id="quote"
      className="py-24 sm:py-32 bg-[#E5E5EA]"
      data-testid="quote-section"
    >
      <div className="max-w-[1440px] mx-auto px-6 sm:px-10">
        <motion.div {...fadeUp} className="max-w-3xl">
          <p className="text-[12px] uppercase tracking-[0.36em] text-[#011233] font-semibold">
            Get a quote
          </p>
          <h2 className="mt-5 font-heading font-bold text-4xl sm:text-6xl text-[#011233] tracking-tight leading-[1.02]">
            Tell us about you.
            <br />
            We&apos;ll handle the rest.
          </h2>
          <p className="mt-6 text-base sm:text-lg text-slate-600 leading-relaxed">
            Share a few details and a licensed agent will reach out with
            carrier-by-carrier quotes tailored to your situation — usually within one
            business day.
          </p>
        </motion.div>

        <div className="mt-14 grid lg:grid-cols-12 gap-8">
          <motion.div {...fadeUp} className="lg:col-span-4">
            <div className="rounded-[28px] bg-[#011233] text-white p-9 sm:p-10 h-full flex flex-col">
              <p className="text-[11px] uppercase tracking-[0.32em] text-[#B5BAC2] font-semibold">
                Talk to a real person
              </p>
              <p className="mt-5 font-heading font-bold text-2xl sm:text-3xl leading-tight">
                One call. Multiple carriers.
                <br />
                <span className="text-[#B5BAC2]">One agent who works for you.</span>
              </p>

              <div className="mt-10 space-y-5 text-[14px] flex-1">
                <QuoteContactItem icon={Phone} label="Toll-free" value={SITE.toll_free} href={`tel:${SITE.toll_free_tel}`} />
                <QuoteContactItem icon={Phone} label="Office" value={`1 ${SITE.phone}`} href={`tel:${SITE.phone.replace(/[^\d]/g, "")}`} />
                <QuoteContactItem icon={Printer} label="Fax" value={SITE.fax} />
                <QuoteContactItem icon={Mail} label="Email" value={SITE.email} href={`mailto:${SITE.email}`} />
                <QuoteContactItem
                  icon={MapPin}
                  label="Office"
                  value={`${SITE.address_line} ${SITE.address_city}`}
                />
              </div>

              <div className="mt-10 pt-6 border-t border-white/15">
                <p className="text-[11px] uppercase tracking-[0.22em] text-white/70 mb-4 font-semibold">
                  Follow us
                </p>
                <div className="kn-social-glow inline-flex">
                  <SocialIcons
                    size={16}
                    linkClassName="text-white hover:text-[#B5BAC2] transition-colors p-2 rounded-full border border-white/20"
                  />
                </div>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={fadeUp.initial}
            whileInView={fadeUp.whileInView}
            viewport={fadeUp.viewport}
            transition={{ ...fadeUp.transition, delay: 0.1 }}
            className="lg:col-span-8"
          >
            <EmbeddedForm />
          </motion.div>
        </div>
      </div>
    </section>
  );
}
